import { LinkBreakIcon, LinkSimpleIcon, NotebookIcon } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { OverflowMenu, OverflowMenuItem } from '@/components/overflow-menu';
import { StatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { entryTotalHours } from '@/features/logbook/columns';
import { draftLogbookEntryForTrack } from '@/features/logbook/draft';
import { formatDecimalHours } from '@/features/logbook/time';
import { tracks } from '@/lib/db';
import { formatCalendarDate } from '@/lib/format';

import {
  CANDIDATE_REASON_LABEL,
  type EntryCandidate,
  linkTrackToFlight,
  suggestEntriesForTrack,
  unlinkTrackFromFlight,
} from './link';
import type { FlightDateOptions } from './FlightStats';
import type { FlightSummary } from './summary';

/**
 * The logbook half of a flight's page: what this flight is worth in the
 * logbook, and the link between the two made visible and editable.
 *
 * Automatic matching on import is the app's main trick, but it is a guess, so
 * the link it made has to be something a pilot can see and undo — and a
 * hand-written entry transcribed long after the fact has to be joinable to a
 * track by hand. Both live here.
 */

function HoursCell({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-muted-foreground text-[0.625rem] font-medium tracking-wider uppercase">
        {label}
      </dt>
      <dd className="figure text-foreground text-base font-semibold">
        {value === 0 ? '—' : formatDecimalHours(value)}
      </dd>
    </div>
  );
}

export interface LogbookPanelProps {
  summary: FlightSummary;
  dateOptions?: FlightDateOptions;
}

export function LogbookPanel({ summary, dateOptions = {} }: LogbookPanelProps) {
  const { entry, flight, track, mergedIntoEntryId } = summary;
  const [candidates, setCandidates] = useState<EntryCandidate[] | null>(null);
  const [busy, setBusy] = useState(false);

  // Narrowed to the flight itself, not a boolean: the unlink button needs the
  // record, and a separate `flight !== undefined` guard beside the boolean is
  // the kind of dead check that reads as if it could fire.
  const linkedFlight = flight?.trackId !== undefined && track !== undefined ? flight : undefined;

  const createDraft = async () => {
    if (track === undefined) {
      return;
    }
    setBusy(true);
    try {
      const points = await tracks.getPoints(track.id);
      if (points !== undefined) {
        await draftLogbookEntryForTrack(track, points);
      }
    } finally {
      setBusy(false);
    }
  };

  const openPicker = async () => {
    if (track === undefined) {
      return;
    }
    setBusy(true);
    try {
      setCandidates(
        await suggestEntriesForTrack({
          startTime: track.startTime,
          ...(track.registration === undefined ? {} : { registration: track.registration }),
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="border-border bg-surface rounded-container space-y-3 border p-3"
      aria-labelledby="logbook-panel-heading"
      data-testid="logbook-panel"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id="logbook-panel-heading"
          className="text-muted-foreground text-[0.625rem] font-medium tracking-wider uppercase"
        >
          Logbook entry
        </h2>
        {entry === undefined ? (
          mergedIntoEntryId === undefined ? null : (
            <StatusBadge status="merged" />
          )
        ) : (
          <StatusBadge status={entry.isDraft ? 'draft' : 'approved'} />
        )}
      </div>

      {entry === undefined && mergedIntoEntryId !== undefined ? (
        /*
         * This recording's entry was merged into another one: the flight was
         * flown once and recorded twice, and its hours are counted on that
         * other line. Offering to draft a fresh entry here would put them in
         * the logbook a second time, so the only thing on offer is the way
         * across to where they went.
         */
        <>
          <p className="text-muted-foreground text-sm leading-relaxed">
            This recording was merged into another logbook entry. Its hours, takeoffs and landings
            are counted there.
          </p>
          <Button size="sm" variant="outline" asChild>
            <Link to="/logbook" hash={mergedIntoEntryId}>
              <NotebookIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
              Open the merged entry
            </Link>
          </Button>
        </>
      ) : entry === undefined ? (
        <>
          <p className="text-muted-foreground text-sm leading-relaxed">
            No logbook entry linked to this flight.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {track === undefined ? null : (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  void createDraft();
                }}
              >
                <NotebookIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
                Draft an entry from this track
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                void openPicker();
              }}
            >
              <LinkSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
              Link an existing entry
            </Button>
          </div>
        </>
      ) : (
        <>
          <dl className="grid grid-cols-4 gap-2">
            <HoursCell label="PIC" value={entry.time.picHr} />
            <HoursCell label="Dual" value={entry.time.dualHr} />
            <HoursCell label="Night" value={entry.time.nightHr} />
            <HoursCell
              label="Instr"
              value={entry.time.actualInstrumentHr + entry.time.simulatedInstrumentHr}
            />
          </dl>
          <p className="text-muted-foreground text-xs">
            {formatCalendarDate(entry.date, dateOptions)}
            {' · '}
            <span className="tabular">{formatDecimalHours(entryTotalHours(entry))}</span> total
            {entry.from === undefined && entry.to === undefined
              ? ''
              : ` · ${entry.from ?? '—'} → ${entry.to ?? '—'}`}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant={entry.isDraft ? 'primary' : 'outline'} asChild>
              <Link to="/logbook" hash={entry.id}>
                <NotebookIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
                {entry.isDraft ? 'Review & confirm' : 'Open in logbook'}
              </Link>
            </Button>
            {linkedFlight === undefined ? null : (
              <span className="ml-auto">
                <OverflowMenu label="More logbook actions">
                  <OverflowMenuItem
                    disabled={busy}
                    onSelect={() => {
                      setBusy(true);
                      void unlinkTrackFromFlight(linkedFlight.id).finally(() => {
                        setBusy(false);
                      });
                    }}
                  >
                    <LinkBreakIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
                    Break the link
                  </OverflowMenuItem>
                </OverflowMenu>
              </span>
            )}
          </div>
        </>
      )}

      {candidates === null ? null : (
        <div
          className="border-border rounded-container space-y-2 border p-2"
          data-testid="entry-picker"
        >
          <p className="text-muted-foreground text-xs">
            Entries with no track of their own, closest match first.
          </p>
          {candidates.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              Every logbook entry already has a track. Nothing to link this one to.
            </p>
          ) : (
            <ul className="space-y-1">
              {candidates.map((candidate) => (
                <li key={candidate.entry.id}>
                  <button
                    type="button"
                    disabled={busy || track === undefined}
                    className="hover:bg-surface-sunken rounded-control flex w-full items-baseline justify-between gap-3 px-2 py-1.5 text-left text-sm"
                    onClick={() => {
                      if (track === undefined) {
                        return;
                      }
                      setBusy(true);
                      void linkTrackToFlight(candidate.flight.id, track.id)
                        .then(() => {
                          setCandidates(null);
                        })
                        .finally(() => {
                          setBusy(false);
                        });
                    }}
                  >
                    <span className="text-foreground">
                      {formatCalendarDate(candidate.entry.date, dateOptions)}
                      {candidate.entry.registration === undefined
                        ? ''
                        : ` · ${candidate.entry.registration}`}
                      {candidate.entry.from === undefined && candidate.entry.to === undefined
                        ? ''
                        : ` · ${candidate.entry.from ?? '—'} → ${candidate.entry.to ?? '—'}`}
                    </span>
                    <span className="text-muted-foreground shrink-0 text-xs">
                      {CANDIDATE_REASON_LABEL[candidate.reason]}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setCandidates(null);
            }}
          >
            Cancel
          </Button>
        </div>
      )}
    </section>
  );
}
